import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { AccessibilityProvider } from '@/contexts/AccessibilityContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import RegisterRequiredRoute from '@/components/RegisterRequiredRoute';
import AppLayout from '@/components/AppLayout';
import VisitTracker from '@/components/VisitTracker';
import PwaAutoLogout from '@/components/pwa/PwaAutoLogout';
import MobileBrandStrip from '@/components/MobileBrandStrip';

// Website Pages
import HomePage from '@/pages/website/HomePage';
import HomePagePreviewV3 from '@/pages/website/HomePagePreviewV3';
import HomePagePreviewV1 from '@/pages/website/HomePagePreviewV1';
import HomePagePreviewV2 from '@/pages/website/HomePagePreviewV2';
import HomePageV2 from '@/pages/website/HomePageV2';
import CabinetHomePage from '@/pages/website/CabinetHomePage';
import HyperventilationPage from '@/pages/website/HyperventilationPage';
import DysfunctionalBreathingPage from '@/pages/website/DysfunctionalBreathingPage';
import CovidLongPage from '@/pages/website/CovidLongPage';
import ExercisesInfoPage from '@/pages/website/ExercisesInfoPage';
import ContactPage from '@/pages/website/ContactPage';
import MonEspacePage from '@/pages/website/MonEspacePage';
import WeeklyProgramPage from '@/pages/website/WeeklyProgramPage';
import MonProgrammePage from '@/pages/website/MonProgrammePage';
import RespiratoryMechanicsPage from '@/pages/website/RespiratoryMechanicsPage';
import OptimalBreathingPage from '@/pages/website/OptimalBreathingPage';
import ButeykoMethodPage from '@/pages/website/ButeykoMethodPage';
import HeartCoherencePage from '@/pages/website/HeartCoherencePage';
import YouTubePage from '@/pages/website/YouTubePage';
import CausesPage from '@/pages/website/CausesPage';
import CausesPagePreviewV1 from '@/pages/website/CausesPagePreviewV1';
import EvaluationPage from '@/pages/website/EvaluationPage';
import DocumentsPage from '@/pages/website/DocumentsPage';
import ProtectedDocumentViewer from '@/pages/website/ProtectedDocumentViewer';
import AsthmaPage from '@/pages/website/AsthmaPage';
import NerfVaguePage from '@/pages/website/NerfVaguePage';
import MobilityMovementPage from '@/pages/website/MobilityMovementPage';
import RespiratoryMuscleTrainingPage from '@/pages/website/RespiratoryMuscleTrainingPage';

// App Pages
import BreathingAppLandingPage from '@/pages/app/BreathingAppLandingPage';
import ExercisesHubPage from '@/pages/app/ExercisesHubPage';
import ChallengesIntroPage from '@/pages/app/ChallengesIntroPage';
import OxygenAdvantagePage from '@/pages/app/OxygenAdvantagePage';
import EmailPreferencesPage from '@/pages/app/EmailPreferencesPage';
import AccessibilitySettingsPage from '@/pages/app/AccessibilitySettingsPage';
import LoginPage from '@/pages/app/LoginPage';
import ForgotPasswordPage from '@/pages/app/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/app/ResetPasswordPage';
import BreathingAppPage from '@/pages/app/BreathingAppPage';
import HistoryPage from '@/pages/app/HistoryPage';
import PatternsPage from '@/pages/app/PatternsPage';
import AdminPage from '@/pages/app/AdminPage';
import AdminUserHistoryPage from '@/pages/app/AdminUserHistoryPage';
import AdminWeeklyProgramPage from '@/pages/app/AdminWeeklyProgramPage';
import PublicWeeklyProgramPage from '@/pages/website/PublicWeeklyProgramPage';
import BoltTestPage from '@/pages/app/BoltTestPage';

import '@/App.css';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

function App() {
  // Protection contre les captures d'écran
  useEffect(() => {
    // Désactiver le clic droit sauf sur les champs de saisie
    const handleContextMenu = (e) => {
      // Autoriser le clic droit sur les champs input, textarea et éléments éditables
      const tagName = e.target.tagName.toLowerCase();
      const isEditable = e.target.isContentEditable;
      const isInputField = tagName === 'input' || tagName === 'textarea' || isEditable;
      
      if (isInputField) {
        return true; // Autoriser le clic droit sur les champs de saisie
      }
      
      e.preventDefault();
      return false;
    };

    // Désactiver les touches de capture d'écran et outils développeur
    const handleKeyDown = (e) => {
      // PrintScreen, F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === 'PrintScreen' ||
        e.keyCode === 44 ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'u')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Désactiver le drag & drop des images
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AccessibilityProvider>
          <VisitTracker />
          <PageViewTracker />
          <SessionStartTracker />
          <PwaAutoLogout />
          <AquaticThemeObserver />
          <MobileBrandStrip />
          <Routes>
            {/* Website Routes */}
            <Route path="/" element={<CabinetHomePage />} />
            <Route path="/respiration" element={<RegisterRequiredRoute><HomePageV2 /></RegisterRequiredRoute>} />
            <Route path="/preview-v3" element={<HomePagePreviewV3 />} />
            <Route path="/preview-v1" element={<HomePagePreviewV1 />} />
            <Route path="/preview-v2" element={<HomePagePreviewV2 />} />
            <Route path="/site-v2" element={<HomePageV2 />} />
            <Route path="/old-home" element={<HomePage />} />
            <Route path="/hyperventilation" element={<RegisterRequiredRoute><HyperventilationPage /></RegisterRequiredRoute>} />
            <Route path="/respiration-dysfonctionnelle" element={<RegisterRequiredRoute><DysfunctionalBreathingPage /></RegisterRequiredRoute>} />
            <Route path="/covid-long" element={<RegisterRequiredRoute><CovidLongPage /></RegisterRequiredRoute>} />
            <Route path="/mecanique-respiratoire" element={<RegisterRequiredRoute><RespiratoryMechanicsPage /></RegisterRequiredRoute>} />
            <Route path="/respiration-optimale" element={<RegisterRequiredRoute><OptimalBreathingPage /></RegisterRequiredRoute>} />
            <Route path="/methode-buteyko" element={<RegisterRequiredRoute><ButeykoMethodPage /></RegisterRequiredRoute>} />
            <Route path="/coherence-cardiaque" element={<RegisterRequiredRoute><HeartCoherencePage /></RegisterRequiredRoute>} />
            <Route path="/causes-troubles-respiratoires" element={<RegisterRequiredRoute><CausesPage /></RegisterRequiredRoute>} />
            <Route path="/causes-preview-v1" element={<CausesPagePreviewV1 />} />
            <Route path="/youtube" element={<RegisterRequiredRoute><YouTubePage /></RegisterRequiredRoute>} />
            <Route path="/evaluer-respiration" element={<RegisterRequiredRoute><EvaluationPage /></RegisterRequiredRoute>} />
            <Route path="/documents" element={
              <ProtectedRoute>
                <DocumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/view-document" element={<ProtectedDocumentViewer />} />
            <Route path="/asthme" element={<AsthmaPage />} />
            <Route path="/nerf-vague" element={<NerfVaguePage />} />
            <Route path="/mobilite-mouvement" element={<MobilityMovementPage />} />
            <Route path="/entrainement-muscles-respiratoires" element={<RespiratoryMuscleTrainingPage />} />
            <Route path="/exercices" element={<ExercisesInfoPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/mon-espace" element={<ProtectedRoute><MonEspacePage /></ProtectedRoute>} />
            <Route path="/mon-espace/programme" element={<ProtectedRoute><WeeklyProgramPage /></ProtectedRoute>} />
            <Route path="/mon-espace/mon-programme" element={<ProtectedRoute><MonProgrammePage /></ProtectedRoute>} />
            
            {/* Breathing App Routes */}
            <Route path="/app-landing" element={<BreathingAppLandingPage />} />
            <Route path="/breathing" element={
              <ProtectedRoute>
                <AppLayout><ExercisesHubPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/breathing/challenges-intro" element={
              <ProtectedRoute>
                <AppLayout><ChallengesIntroPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/breathing/oxygen-advantage" element={
              <ProtectedRoute>
                <AppLayout><OxygenAdvantagePage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings/emails" element={
              <ProtectedRoute>
                <AppLayout><EmailPreferencesPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings/accessibilite" element={
              <ProtectedRoute>
                <AppLayout><AccessibilitySettingsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/breathing/practice" element={
              <ProtectedRoute>
                <AppLayout><BreathingAppPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/breathing/bolt" element={
              <ProtectedRoute>
                <AppLayout><BoltTestPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/app" element={
              <ProtectedRoute>
                <AppLayout><BreathingAppPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <AppLayout><HistoryPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/patterns" element={
              <ProtectedRoute>
                <AppLayout><PatternsPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AppLayout><AdminPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/user/:userId" element={
              <ProtectedRoute>
                <AppLayout><AdminUserHistoryPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users/:userId/weekly-program" element={
              <ProtectedRoute>
                <AppLayout><AdminWeeklyProgramPage /></AppLayout>
              </ProtectedRoute>
            } />
            <Route path="/programme-partage/:token" element={<PublicWeeklyProgramPage />} />
          </Routes>
          </AccessibilityProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;

// Sets body[data-aquatic="true"] on content pages (all except home & app routes)
// so global CSS in index.css can theme the whole page (wave bg, fonts, glass cards).
const AQUATIC_PATHS = new Set([
  '/hyperventilation',
  '/respiration-dysfonctionnelle',
  '/covid-long',
  '/mecanique-respiratoire',
  '/respiration-optimale',
  '/methode-buteyko',
  '/coherence-cardiaque',
  '/causes-troubles-respiratoires',
  '/youtube',
  '/evaluer-respiration',
  '/documents',
  '/asthme',
  '/nerf-vague',
  '/mobilite-mouvement',
  '/entrainement-muscles-respiratoires',
  '/exercices',
  '/contact',
]);
function AquaticThemeObserver() {
  const location = useLocation();
  useEffect(() => {
    const isAquatic = AQUATIC_PATHS.has(location.pathname);
    if (isAquatic) {
      document.body.dataset.aquatic = 'true';
    } else {
      delete document.body.dataset.aquatic;
    }
  }, [location.pathname]);
  return null;
}

// Fires a lightweight analytics event on every route change for authenticated users.
// Deduplicates same-path in quick succession and swallows all errors so it never breaks navigation.
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // Only track for logged-in users
    const path = location.pathname + (location.search || '');
    const key = `pv_last:${path}`;
    const now = Date.now();
    const last = Number(sessionStorage.getItem(key) || 0);
    if (now - last < 4000) return; // Debounce back-to-back triggers
    sessionStorage.setItem(key, String(now));
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ event_type: 'page_view', path, referrer: document.referrer || null }),
      keepalive: true,
    }).catch(() => {});
  }, [location.pathname, location.search]);
  return null;
}

// Fires a "login" analytics event as soon as an authenticated user opens the site
// (new tab / hard refresh) OR comes back to the tab after being away >30 min.
// Also fires when the user transitions from unauthenticated → authenticated within
// the same tab (fresh login inside the SPA). Patients stay logged in for weeks:
// without this we would only ever count the initial authentication and never their
// daily visits.
function SessionStartTracker() {
  const { token } = useAuth();
  useEffect(() => {
    const SESSION_STORAGE_KEY = 'session_start_sent_at';
    const IDLE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`;

    const fire = () => {
      const currentToken = token || localStorage.getItem('token');
      if (!currentToken) return;
      const now = Date.now();
      const last = Number(sessionStorage.getItem(SESSION_STORAGE_KEY) || 0);
      // Only fire once per tab session AND only if last emission > 30 min ago.
      if (last && now - last < IDLE_THRESHOLD_MS) return;
      sessionStorage.setItem(SESSION_STORAGE_KEY, String(now));
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
        body: JSON.stringify({ event_type: 'login' }),
        keepalive: true,
      }).catch(() => {});
    };

    // 1. On mount OR whenever the auth token changes (e.g. fresh login inside SPA)
    fire();

    // 2. Also fire when the tab becomes visible again after being hidden
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [token]);
  return null;
}
